import type { BusinessCenterTaskRecord, BusinessCenterTaskStatus, BusinessCenterTaskPriority } from "../../lib/types";
import { createBusinessCenterOperationalStateStore } from "../../lib/businessCenterOperationalStateStore";

export const taskStatusOptions: BusinessCenterTaskStatus[] = ["Not Started", "In Progress", "Waiting", "Complete", "On Hold"];
export const taskPriorityOptions: BusinessCenterTaskPriority[] = ["Low", "Medium", "High", "Urgent"];

const tasksStore = createBusinessCenterOperationalStateStore<BusinessCenterTaskRecord[]>({
  moduleKey: "business_center_tasks",
  scope: { scopeKey: "tasks-center", scopeType: "platform" },
  seed: createSeedTasks,
  normalize: (tasks) => tasks.map(normalizeTask),
  isEmpty: (tasks) => !Array.isArray(tasks) || tasks.length === 0,
});

export function loadBusinessCenterTasks(): BusinessCenterTaskRecord[] {
  return tasksStore.read();
}

export async function refreshBusinessCenterTasks() {
  return tasksStore.refresh();
}

export function loadBusinessCenterTasksForBrand(brand: BusinessCenterTaskRecord["brand"]) {
  return loadBusinessCenterTasks().filter((task) => task.brand === brand);
}

export function createBusinessCenterTask(input: BusinessCenterTaskInput): BusinessCenterTaskRecord {
  const now = new Date().toISOString();
  const taskCount = loadBusinessCenterTasks().filter((task) => task.brand === input.brand).length + 1;
  return normalizeTask({
    id: crypto.randomUUID(),
    taskNumber: `BCT-${String(taskCount).padStart(4, "0")}`,
    brand: input.brand,
    clientId: input.clientId ?? null,
    clientName: input.clientName || "Unlinked Client",
    projectId: input.projectId ?? null,
    projectName: input.projectName || "Unlinked Project",
    taskTitle: input.taskTitle || "Untitled Task",
    description: input.description || "",
    dueDate: input.dueDate ?? null,
    status: input.status || "Not Started",
    priority: input.priority || "Medium",
    assignedMember: input.assignedMember || "Team Member Placeholder",
    notes: input.notes || "",
    createdAt: now,
    updatedAt: now,
  });
}

export function upsertBusinessCenterTask(nextTask: BusinessCenterTaskRecord) {
  const current = loadBusinessCenterTasks();
  const normalized = normalizeTask({ ...nextTask, updatedAt: new Date().toISOString() });
  const exists = current.some((task) => task.id === normalized.id);
  const tasks = exists ? current.map((task) => (task.id === normalized.id ? normalized : task)) : [normalized, ...current];
  saveBusinessCenterTasks(tasks);
  return tasks;
}

export function updateBusinessCenterTask(taskId: string, changes: Partial<BusinessCenterTaskRecord>) {
  const current = loadBusinessCenterTasks();
  const tasks = current.map((task) =>
    task.id === taskId
      ? normalizeTask({
          ...task,
          ...changes,
          updatedAt: new Date().toISOString(),
        })
      : task,
  );
  saveBusinessCenterTasks(tasks);
  return tasks;
}

export function removeBusinessCenterTask(taskId: string) {
  const tasks = loadBusinessCenterTasks().filter((task) => task.id !== taskId);
  saveBusinessCenterTasks(tasks);
  return tasks;
}

function saveBusinessCenterTasks(tasks: BusinessCenterTaskRecord[]) {
  tasksStore.write(tasks);
}

function normalizeTask(task: BusinessCenterTaskRecord): BusinessCenterTaskRecord {
  return {
    ...task,
    brand: task.brand || "truevance_connect",
    clientId: task.clientId ?? null,
    clientName: task.clientName || "Unlinked Client",
    projectId: task.projectId ?? null,
    projectName: task.projectName || "Unlinked Project",
    taskTitle: task.taskTitle || "Untitled Task",
    description: task.description || "",
    dueDate: task.dueDate ?? null,
    status: taskStatusOptions.includes(task.status) ? task.status : "Not Started",
    priority: taskPriorityOptions.includes(task.priority) ? task.priority : "Medium",
    assignedMember: task.assignedMember || "Team Member Placeholder",
    notes: task.notes || "",
    createdAt: task.createdAt || new Date().toISOString(),
    updatedAt: task.updatedAt || new Date().toISOString(),
  };
}

function createSeedTasks(): BusinessCenterTaskRecord[] {
  const now = new Date().toISOString();
  return [
    normalizeTask({
      id: crypto.randomUUID(),
      taskNumber: "BCT-0001",
      brand: "truevance_connect",
      clientName: "Northstar Ministries",
      projectName: "Northstar Quick Wins Roadmap",
      taskTitle: "Draft leadership review checklist",
      description: "Prepare the next review steps for the assessment-to-proposal handoff.",
      dueDate: "2026-06-21",
      status: "In Progress",
      priority: "High",
      assignedMember: "Team Member Placeholder",
      notes: "Use this task to keep the discovery work moving toward proposal readiness.",
      createdAt: now,
      updatedAt: now,
    }),
    normalizeTask({
      id: crypto.randomUUID(),
      taskNumber: "BCT-0002",
      brand: "truevance_connect",
      clientName: "Summit Event Group",
      projectName: "Operations Dashboard Build",
      taskTitle: "Collect implementation notes",
      description: "Gather the remaining notes needed for the project build phase.",
      dueDate: "2026-06-19",
      status: "Waiting",
      priority: "Medium",
      assignedMember: "Team Member Placeholder",
      notes: "Waiting on internal feedback before moving to execution.",
      createdAt: now,
      updatedAt: now,
    }),
    normalizeTask({
      id: crypto.randomUUID(),
      taskNumber: "BCT-0003",
      brand: "truevance_connect",
      clientName: "Northstar Ministries",
      projectName: "Northstar Quick Wins Roadmap",
      taskTitle: "Confirm client follow-up date",
      description: "Verify the next client touchpoint and update the operating timeline.",
      dueDate: "2026-06-17",
      status: "Complete",
      priority: "Low",
      assignedMember: "Team Member Placeholder",
      notes: "Completed but kept visible as a workflow reference.",
      createdAt: now,
      updatedAt: now,
    }),
    normalizeTask({
      id: crypto.randomUUID(),
      taskNumber: "BCT-1001",
      brand: "kreatethedate",
      clientName: "Amara Thompson",
      projectName: "Memory Media Vault Setup",
      taskTitle: "Review upload instructions",
      description: "Make sure client-facing instructions are ready before the next event handoff.",
      dueDate: "2026-06-20",
      status: "Not Started",
      priority: "High",
      assignedMember: "Team Member Placeholder",
      notes: "This keeps the Memory Media Vault admin flow visible without changing behavior.",
      createdAt: now,
      updatedAt: now,
    }),
  ];
}

export interface BusinessCenterTaskInput {
  brand: BusinessCenterTaskRecord["brand"];
  clientId?: string | null;
  clientName?: string;
  projectId?: string | null;
  projectName?: string;
  taskTitle: string;
  description: string;
  dueDate?: string | null;
  status?: BusinessCenterTaskStatus;
  priority?: BusinessCenterTaskPriority;
  assignedMember?: string;
  notes?: string;
}
